import { Dispatch, useContext } from 'react';

import {
  NotificationsDispatchContext,
  NotificationsStateContext
} from '../components/notificationsProvider/contexts/notifications.context';
import { NotificationsAction } from '../components/notificationsProvider/notificationsProvider.enums';
import { NotificationsActions, NotificationsState } from '../components/notificationsProvider/notificationsProvider.types';
import { SmallDialogSettings, ToastSettings, UseNotifications } from '../interfaces';

export const useNotifications: () => UseNotifications = (): UseNotifications => {
  const notificationsState: NotificationsState = useContext(NotificationsStateContext);
  const notificationsDispatch: Dispatch<NotificationsActions> = useContext(NotificationsDispatchContext);

  if (!notificationsState || !notificationsDispatch) {
    throw new Error('Notifications context error');
  }

  const addToast: (data: ToastSettings) => void = (data: ToastSettings): void => {
    const closeAction: () => void = (): void => {
      if (data.onClose) {
        data.onClose();
      }
      setTimeout(
        (): void => {
          notificationsDispatch({ type: NotificationsAction.RemoveToast, payload: data.content });
        },
        500
      );
    };

    notificationsDispatch({
      type: NotificationsAction.AddToast,
      payload: {
        ...data,
        onClose: closeAction
      }
    });
  };

  const showSmallDialog: (data: SmallDialogSettings) => void = (data: SmallDialogSettings): void => {
    const accept: () => void = (): void => {
      if (data.acceptButton?.action) {
        data.acceptButton.action();
      }
      notificationsDispatch({ type: NotificationsAction.SetSmallDialogIsLoading });
    };

    const cancel: () => void = (): void => {
      if (data.cancelButton?.action) {
        data.cancelButton.action();
      }
      notificationsDispatch({ type: NotificationsAction.HideSmallDialog });
    };

    const close: () => void = (): void => {
      if (data.closeButtonAction) {
        data.closeButtonAction();
      }
      notificationsDispatch({ type: NotificationsAction.HideSmallDialog });
    };

    notificationsDispatch({
      type: NotificationsAction.ShowSmallDialog,
      payload: {
        ...data,
        isActive: true,
        acceptButton: {
          label: '',
          ...data.acceptButton,
          action: accept
        },
        cancelButton: {
          label: '',
          ...data.cancelButton,
          action: cancel,
        },
        closeButtonAction: close,
      }
    });
  };

  const hideSmallDialog: () => void = (): void => {
    notificationsDispatch({ type: NotificationsAction.HideSmallDialog });
  };

  const updateAcceptButtonLabel: (label: string) => void = (label: string): void => {
    notificationsDispatch({ type: NotificationsAction.UpdateAcceptButtonLabel, payload: label });
  };

  return {
    notificationsState,
    addToast,
    showSmallDialog,
    hideSmallDialog,
    updateAcceptButtonLabel,
  };
};
